import React, {useEffect} from 'react';
import {useI18nInitialization} from '@nl/i18n';
import i18nLocales from '@nl/i18n-locales';
import ImageProxy from '@nl/image-proxy';
import {MainRoute} from '@nl/core';
import {now} from '@nl/neu';
import {hot} from 'react-hot-loader/root';
import {Error, useActions, useDeepEffect} from '@nl/ui';
import {shallowEqual, useSelector} from 'react-redux';
import useScrollToTop from './hooks/useScrollToTop';
import useTrackers from './hooks/useTrackers';
import useResetPersonal from './hooks/useResetPersonal';
import useFavorite from './hooks/useFavorite';
import useAdInitialization from './hooks/useAdInitialization';
import useEvent from './hooks/useEvent';
import routes from './router';
import Header from './pages/Header';
import FreeSampleCountdown from './components/freesample/FreeSampleCountdown';
import projectLocales from './assets/i18n';
import {PageLoading, ContentLoading} from './components/loading';
import AppModal from './components/modal/AppModal';
import {init} from './utils/auth';
import {setUserStatus, setInitialized, setLocaleLanguage, setHideScore} from './store/actions/client';
import useHideNavParam from './hooks/useHideNavParam';
import useDayJsInitialization from './hooks/useDayJsInitialization';

function App() {
	const [
		setClientAction,
		setInitializedAction,
		setLocaleLanguageAction,
		setHideScoreAction,
	] = useActions([setUserStatus, setInitialized, setLocaleLanguage, setHideScore]);
	const {
		config: {
			I18N_URL,
			cdn,
			imageConfig,
			isDomestic,
		}, initialized, locale, timeOffset,
	} = useSelector(
			(state) => ({
				config: state.config.data,
				initialized: state.client.initialized,
				locale: state.client.data.locale,
				timeOffset: state.client.data.timeOffset,
			}),
			shallowEqual,
	);
	const {data} = useSelector((state) => state.config, shallowEqual);
	const {
		failed: configLoadFailed,
		failedGeo: geoBlocked,
	} = useSelector((state) => state.config, shallowEqual);

	useAdInitialization();
	useTrackers();
	useResetPersonal();
	useFavorite();
	useEvent();
	useScrollToTop();
	useDayJsInitialization();
	useHideNavParam();
	useI18nInitialization([i18nLocales, projectLocales], locale, `${I18N_URL}${locale}.json`);

	useEffect(() => {
		if (!isNaN(timeOffset)) {
			const time = now().getTime() - timeOffset;
			now(time);
		}
	}, [timeOffset]);

	useDeepEffect(() => {
		if (cdn && imageConfig) {
			ImageProxy.init(cdn.content, imageConfig);
		}
	}, [cdn, imageConfig]);

	if (configLoadFailed) {
		if (geoBlocked) {
			// geo blocked
			return (<Error errKey="geo_blocked" />);
		}
		// config load failed
		return (<Error errKey="config_failed" />);
	}

	init(isDomestic, false, setClientAction, setInitializedAction, setLocaleLanguageAction, data,
			setHideScoreAction);
	if (!initialized) {
		return (<PageLoading />);
	}

	return (
			<>
				{!window.IS_WEB_VIEW && <Header />}
				<FreeSampleCountdown />
				<MainRoute Loading={ContentLoading} routes={routes} />
				<AppModal />
			</>
	);
}

export default hot(App);
